import { Wifi, Phone, Tv, Package, ArrowRight, HandCoins, CalendarClock } from "lucide-react";
import { PACKAGE_GROUPS, WHATSAPP_LINK, INDIHOME_LOGO, migrationFee, MIGRATION_FEE_TIERS } from "../constants/config";
import { SavingsCalculator } from "./SavingsCalculator";
import { ProrataCalculator } from "./ProrataCalculator";

const GROUP_ICONS = { "1p": Wifi, "2p-telp": Phone, "2p-tv": Tv, "3p": Package };

export const Packages = () => {
  return (
    <section
      id="paket"
      data-testid="packages-section"
      className="py-20 md:py-32 bg-white"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <div className="flex items-center gap-3">
              <p className="text-xs md:text-sm font-semibold uppercase tracking-widest text-[#E60000]">
                Paket Retensi
              </p>
              <img
                src={INDIHOME_LOGO}
                alt="IndiHome"
                className="h-5 w-auto opacity-90"
                loading="lazy"
              />
            </div>
            <h2 className="font-heading text-2xl sm:text-3xl lg:text-4xl tracking-tight font-bold text-gray-900 mt-4">
              Pilih Paket yang Bikin Tagihan Turun
            </h2>
            <p className="text-base text-gray-600 mt-4 leading-relaxed">
              Harga paket retensi per bulan. Harga belum termasuk biaya admin
              Rp 7.500 dan PPN 11% — hitung totalnya dengan kalkulator di bawah.
            </p>
          </div>
          <a
            href={WHATSAPP_LINK}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="packages-check-price-button"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#E60000] hover:gap-3 transition-[gap]"
          >
            Tanya paket via WhatsApp
            <ArrowRight className="w-4 h-4" />
          </a>
        </div>

        <div className="grid md:grid-cols-2 gap-6 mt-14">
          {PACKAGE_GROUPS.map((group) => {
            const Icon = GROUP_ICONS[group.id];
            return (
              <div
                key={group.id}
                data-testid={`package-group-${group.id}`}
                className="bg-white rounded-2xl border border-black/10 overflow-hidden hover:-translate-y-1 hover:shadow-lg transition-[transform,box-shadow] duration-300"
              >
                <div className="flex items-center gap-3 bg-[#E60000] px-7 py-5">
                  <Icon className="w-6 h-6 text-white" strokeWidth={2} />
                  <h3 className="font-heading text-lg font-semibold text-white">
                    {group.name}
                  </h3>
                </div>
                <ul className="divide-y divide-gray-100">
                  {group.items.map((item) => (
                    <li
                      key={item.speed}
                      data-testid={`package-item-${group.id}-${item.speed.replace(/\s/g, "")}`}
                      className="flex items-center justify-between px-7 py-3.5"
                    >
                      <div>
                        <span className="font-heading font-semibold text-gray-900">
                          {item.speed}
                        </span>
                        <p className="text-xs text-gray-400 font-medium mt-0.5">
                          Fee jasa Rp {migrationFee(item.speed).toLocaleString("id-ID")}
                        </p>
                      </div>
                      <span className="text-sm font-bold text-[#E60000]">
                        Rp {item.price.toLocaleString("id-ID")}
                        <span className="text-gray-400 font-medium">/bln</span>
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <div
          data-testid="migration-fee-box"
          className="mt-10 bg-gray-950 rounded-2xl p-7 md:p-8 flex flex-col md:flex-row md:items-center gap-6 relative overflow-hidden"
        >
          <span
            aria-hidden="true"
            className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-[#E60000]/20 pointer-events-none"
          />
          <span className="w-12 h-12 rounded-xl bg-green-600 flex items-center justify-center shrink-0 relative">
            <HandCoins className="w-6 h-6 text-white" strokeWidth={2} />
          </span>
          <div className="flex-1 relative">
            <p className="inline-block text-[11px] font-bold uppercase tracking-widest text-green-400 bg-green-400/10 border border-green-400/30 rounded-full px-3 py-1">
              Paling Aman
            </p>
            <h3 className="font-heading text-lg md:text-xl font-bold text-white mt-3">
              Biaya Jasa Migrasi — dibayar setelah migrasi berhasil
            </h3>
            <p className="text-sm text-gray-300 mt-1">
              Tanpa biaya di muka. Fee jasa baru dibayar setelah paket retensi
              kamu aktif.
            </p>
            <div className="flex flex-wrap gap-x-8 gap-y-2 mt-4">
              {MIGRATION_FEE_TIERS.map((t) => (
                <p key={t.label} className="text-sm text-gray-300">
                  <span className="font-medium">{t.label}:</span>{" "}
                  <span className="font-bold text-white">
                    Rp {t.fee.toLocaleString("id-ID")}
                  </span>
                </p>
              ))}
            </div>
          </div>
        </div>

        <div
          data-testid="prorata-info-box"
          className="mt-6 bg-white border border-black/10 rounded-2xl p-7 md:p-8 flex flex-col md:flex-row md:items-center gap-6"
        >
          <span className="w-12 h-12 rounded-xl bg-gray-900 flex items-center justify-center shrink-0">
            <CalendarClock className="w-6 h-6 text-white" strokeWidth={2} />
          </span>
          <div className="flex-1">
            <h3 className="font-heading text-lg font-bold text-gray-900">
              Catatan Sistem Pascabayar IndiHome
            </h3>
            <p className="text-sm text-gray-600 mt-1 leading-relaxed">
              IndiHome memakai sistem pascabayar (bayar di akhir pemakaian),
              jadi pergantian paket dihitung prorata:{" "}
              <span className="font-semibold text-gray-900">bulan pertama</span>{" "}
              setelah migrasi tagihan masih prorata (campuran paket lama dan
              paket baru).{" "}
              <span className="font-semibold text-[#E60000]">
                Bulan kedua setelah migrasi barulah tagihan full sesuai harga
                paket retensi baru.
              </span>
            </p>
          </div>
        </div>

        <p className="text-sm text-gray-500 mt-8 max-w-2xl">
          Ketersediaan paket retensi dicek per nomor IndiHome. Isi form order di
          bawah atau chat WhatsApp untuk cek nomor kamu.
        </p>

        <SavingsCalculator />
        <ProrataCalculator />
      </div>
    </section>
  );
};
